import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  ArrowLeft, WifiOff, Calendar, RefreshCw, AlertCircle, Loader2, Users, Info
} from 'lucide-react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useIntegrationsByPracticeId } from '../../hooks/Integration/useIntegration';
import { useLatestScraperRun } from '../../hooks/Scraper/useLatestScraperRun';
import { formatRunDate } from '../../utils/scraperRun';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../redux/api';

// ── Types ──

type SyncStep = 'idle' | 'importing' | 'done' | 'timeout' | 'error';

const PRACTICE_ID_FALLBACK = '695aad9190a86ce133387bc5';
const PLATFORM = 'simple_practice';

// Poll the scraper-run status every 30s, for at most 5 minutes
const POLL_INTERVAL_MS = 30_000;
const MAX_POLL_ATTEMPTS = 10;

// Make webhook that emails the user when a long-running import completes.
// URL to be provided; the call is skipped while empty.
const IMPORT_TIMEOUT_WEBHOOK_URL = '';

// ── Component ──

const SimplePracticeIntegrationDetails: React.FC = () => {
  const navigate = useNavigate();

  // User data
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const practiceId = user?.["custom:id_practice"] || user?.practice_id || PRACTICE_ID_FALLBACK;
  const cognitoSub = user?.["sub"] || '';

  // Integration data
  const { integration, loading: integrationLoading, error: integrationError } = useIntegrationsByPracticeId(practiceId || '');

  // Latest scraper run (drives Last Import Date)
  const { run: latestRun, refetch: refetchLatestRun } = useLatestScraperRun(practiceId || '');

  // ── State ──
  const [currentStep, setCurrentStep] = useState<SyncStep>('idle');
  const [stepError, setStepError] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState<number>(0);
  const [teamMemberCount, setTeamMemberCount] = useState<number | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollAttemptRef = useRef(0);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Navigation helpers
  const handleBack = () => navigate('/practice/integrations');
  const handleEdit = () => {
    if (integration?.id) navigate(`/practice/integration/simplepractice/form?id=${String(integration.id)}`);
  };
  const handleViewDashboard = () => navigate('/practice/integration/simplepractice');
  const handleGoToWorkforce = () => navigate('/practice/workforce');

  // Reset flow
  const resetFlow = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollAttemptRef.current = 0;
    setCurrentStep('idle');
    setStepError(null);
    setImportProgress(0);
    setTeamMemberCount(null);
  }, []);

  // Notify the Make webhook so the user gets an email once the import finishes.
  // Fire-and-forget: raw fetch (external URL must not receive the API auth header).
  const notifyTimeoutWebhook = useCallback((executionArn: string) => {
    if (!IMPORT_TIMEOUT_WEBHOOK_URL) return;
    fetch(IMPORT_TIMEOUT_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_practice: practiceId,
        id_user: cognitoSub,
        platform: PLATFORM,
        executionArn
      })
    }).catch(err => console.error('Timeout webhook notification failed:', err));
  }, [practiceId, cognitoSub]);

  // ── Poll GET /scraper-run/status/{executionArn} until complete ──
  const startPollingForStatus = useCallback((executionArn: string) => {
    if (pollRef.current) clearInterval(pollRef.current);

    const poll = async () => {
      pollAttemptRef.current += 1;
      if (pollAttemptRef.current > MAX_POLL_ATTEMPTS) {
        if (pollRef.current) clearInterval(pollRef.current);
        setCurrentStep('timeout');
        notifyTimeoutWebhook(executionArn);
        return;
      }

      try {
        const res = await api.get(`/scraper-run/status/${encodeURIComponent(executionArn)}`);
        const body = res.data?.body;
        const run = Array.isArray(body) && body.length > 0 ? body[0] : null;
        if (!run) return; // nothing recorded yet; keep polling

        if (typeof run.progress === 'number') {
          setImportProgress(Math.max(0, Math.min(100, run.progress)));
        }

        if (run.status === 'complete' && run.progress === 100) {
          if (pollRef.current) clearInterval(pollRef.current);
          setTeamMemberCount(typeof run.team_member_count === 'number' ? run.team_member_count : null);
          refetchLatestRun();
          setCurrentStep('done');
          toast.success('Import completed!');
        } else if (run.status === 'failed' || run.status === 'error') {
          if (pollRef.current) clearInterval(pollRef.current);
          setCurrentStep('error');
          setStepError('Import process failed on server.');
        }
      } catch (e) {
        // Transient poll errors are ignored; the attempt cap still applies
      }
    };

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
  }, [notifyTimeoutWebhook, refetchLatestRun]);

  // ── Import Team (trigger & poll) ──
  const handleImportClinicians = useCallback(async () => {
    resetFlow();
    setCurrentStep('importing');

    try {
      const res = await api.post(`/practice/${practiceId}/import-team`, {
        id_practice: practiceId,
        id_user: cognitoSub,
        platform: PLATFORM
      });
      const data = res.data?.body || res.data;

      if (data?.executionArn) {
        pollAttemptRef.current = 0;
        startPollingForStatus(data.executionArn);
      } else {
        throw new Error('No executionArn returned for import');
      }

    } catch (err: any) {
      console.error('Import trigger failed:', err);
      setCurrentStep('error');
      setStepError(err.message || 'Failed to start import.');
      toast.error(err.message || 'Failed to start import.');
    }
  }, [practiceId, cognitoSub, resetFlow, startPollingForStatus]);


  // ── Render ──

  if (integrationLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (integrationError) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh] flex-col">
          <AlertCircle className="w-10 h-10 text-red-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-800">Error</h2>
          <p className="text-gray-500 mb-4">Could not load integration.</p>
          <button onClick={handleBack} className="text-blue-600 hover:underline">Back</button>
        </div>
      </DashboardLayout>
    );
  }

  if (!integration) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh] flex-col">
          <WifiOff className="w-10 h-10 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-800">No Integration</h2>
          <button onClick={() => navigate('/practice/integration/simplepractice/form')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded">
            Set Up Integration
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex-1 overflow-auto bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 min-h-screen">
        <div className="max-w-3xl mx-auto p-6 md:p-8 space-y-6">

          {/* Header */}
          <div className="flex items-center space-x-4">
            <button onClick={handleBack} className="p-2 hover:bg-white/60 rounded-lg transition-all">
              <ArrowLeft className="h-5 w-5 text-gray-500" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{integration.integrationName || 'SimplePractice'}</h1>
              <p className="text-gray-500 text-sm">Integration Details</p>
            </div>
          </div>

          {/* Details Grid */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-5">Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DetailItem icon={<Calendar className="h-4 w-4 text-purple-500" />} label="Last Import Date" value={formatRunDate(latestRun)} />
            </div>
          </div>

          {/* Sync / Actions */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Sync Clinicians</h3>
            <p className="text-sm text-gray-500 mb-6">
              Import your team members from your SimplePractice account.
            </p>

            <button
              onClick={handleImportClinicians}
              disabled={currentStep === 'importing' || currentStep === 'done'}
              className="flex items-center space-x-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${currentStep === 'importing' ? 'animate-spin' : ''}`} />
              <span>Import Clinicians</span>
            </button>

            {currentStep === 'error' && stepError && (
              <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-xl flex items-center space-x-2">
                <AlertCircle className="h-5 w-5" />
                <span>{stepError}</span>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
            <div className="flex gap-3">
              <button onClick={handleEdit} className="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200">Edit Integration</button>
              <button onClick={handleViewDashboard} className="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200">View Dashboard</button>
            </div>
          </div>

        </div>

        {/* ── Modals ── */}

        {/* 1. Importing Progress Modal (blocks all page interaction) */}
        {currentStep === 'importing' && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Importing Team Members...</h3>
              <p className="text-gray-500 mb-6">
                Connecting to SimplePractice to import your team. This can take a few minutes.
              </p>
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-gray-200 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${importProgress}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-gray-700 w-10 text-right">{importProgress}%</span>
              </div>
            </div>
          </div>
        )}

        {/* 2. Timeout Info Modal */}
        {currentStep === 'timeout' && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Info className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Still Working On It</h3>
              <p className="text-gray-500 mb-6">
                This is taking longer than expected. We'll send you an email as soon as the import completes.
              </p>
              <button
                onClick={resetFlow}
                className="w-full py-3 px-4 bg-gray-900 text-white rounded-xl font-medium hover:bg-gray-800 transition"
              >
                OK
              </button>
            </div>
          </div>
        )}

        {/* 3. Import Success Modal */}
        {currentStep === 'done' && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {teamMemberCount !== null ? `${teamMemberCount} Team Members Imported` : 'Import Complete'}
                </h3>
                <p className="text-gray-500 mt-2">
                  Would you like to integrate the imported clinicians into the Therapist Direct Workforce?
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={resetFlow}
                  className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition"
                >
                  No
                </button>
                <button
                  onClick={handleGoToWorkforce}
                  className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition shadow-lg shadow-blue-200"
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
};

// ── Helpers ──
const DetailItem: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="flex items-start space-x-3 bg-gray-50 rounded-xl p-4">
    <div className="mt-0.5">{icon}</div>
    <div>
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-800">{value}</p>
    </div>
  </div>
);

export default SimplePracticeIntegrationDetails;
